import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ErrorText,
  Field,
  Input,
  LocalizedInput,
  Textarea,
  toSum,
  toTiyin,
  type Localized,
} from '@/components/ui';

interface Setting {
  key: string;
  value: unknown;
  group: string;
  description: string | null;
}

interface PickupPoint {
  id: string;
  name: Localized;
  address: Localized;
  lat: number;
  lng: number;
  phone: string | null;
  isOrigin: boolean;
  isActive: boolean;
}

interface Offer {
  id: string;
  version: number;
  content: Localized;
  isActive: boolean;
  publishedAt: string | null;
}

export default function SettingsPage() {
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Настройки</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Значения читает бизнес-логика: цены доставки, сроки удержания, залог и штрафы
        </p>
      </header>

      {error && <ErrorText>{error}</ErrorText>}

      <DeliverySettings onError={setError} />
      <RentalSettings onError={setError} />
      <PickupPoints onError={setError} />
      <OfferEditor onError={setError} />
    </div>
  );
}

/** Денежные настройки хранятся в тийинах, в форме показываются сумами. */
function useSettings() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['settings'],
    queryFn: async () => (await api.get<Setting[]>('/admin/settings')).data,
  });

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => api.put('/admin/settings', { values }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  });

  const map = new Map((query.data ?? []).map((s) => [s.key, s.value]));

  return { query, save, map };
}

function DeliverySettings({ onError }: { onError: (m: string | null) => void }) {
  const { query, save, map } = useSettings();
  const [form, setForm] = useState({ base: '', perKm: '', threshold: '', radius: '', free: false });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!query.data || loaded) return;

    setForm({
      base: toSum(Number(map.get('delivery.base_cost') ?? 0)),
      perKm: toSum(Number(map.get('delivery.cost_per_km') ?? 0)),
      threshold: toSum(Number(map.get('delivery.free_threshold') ?? 0)),
      radius: String(map.get('delivery.max_radius_km') ?? 40),
      free: Boolean(map.get('delivery.free_enabled')),
    });
    setLoaded(true);
  }, [query.data, loaded, map]);

  return (
    <Card
      title="Доставка"
      actions={
        <Button
          disabled={save.isPending}
          onClick={() =>
            save
              .mutateAsync({
                'delivery.base_cost': toTiyin(form.base),
                'delivery.cost_per_km': toTiyin(form.perKm),
                'delivery.free_threshold': toTiyin(form.threshold),
                'delivery.max_radius_km': Number(form.radius),
                'delivery.free_enabled': form.free,
              })
              .then(() => onError(null))
              .catch((e) => onError(errorMessage(e)))
          }
        >
          Сохранить
        </Button>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Базовая стоимость, сум">
          <Input
            value={form.base}
            inputMode="numeric"
            onChange={(e) => setForm({ ...form, base: e.target.value })}
          />
        </Field>
        <Field label="За километр, сум">
          <Input
            value={form.perKm}
            inputMode="numeric"
            onChange={(e) => setForm({ ...form, perKm: e.target.value })}
          />
        </Field>
        <Field label="Бесплатно от суммы, сум">
          <Input
            value={form.threshold}
            inputMode="numeric"
            onChange={(e) => setForm({ ...form, threshold: e.target.value })}
          />
        </Field>
        <Field label="Радиус доставки, км">
          <Input
            value={form.radius}
            inputMode="numeric"
            onChange={(e) => setForm({ ...form, radius: e.target.value })}
          />
        </Field>
      </div>

      <label className="mt-3 flex items-center gap-2 text-sm">
        <Checkbox
          checked={form.free}
          onChange={(e) => setForm({ ...form, free: e.target.checked })}
          className="size-4"
        />
        Доставка бесплатна всегда
      </label>

      <p className="mt-3 text-xs text-neutral-500">
        Расстояние пока считается по прямой с коэффициентом 1,3 — ключа Yandex Maps ещё нет.
      </p>
    </Card>
  );
}

function RentalSettings({ onError }: { onError: (m: string | null) => void }) {
  const { query, save, map } = useSettings();
  const [form, setForm] = useState({
    hold: '',
    cashHold: '',
    buffer: '',
    lateFee: '',
    freeCancel: '',
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!query.data || loaded) return;

    setForm({
      hold: String(map.get('reservation.hold_ttl_minutes') ?? 15),
      cashHold: String(map.get('reservation.cash_hold_ttl_minutes') ?? 1440),
      buffer: String(map.get('rental.default_buffer_days') ?? 3),
      lateFee: String(map.get('fine.late_fee_multiplier') ?? 1.5),
      freeCancel: String(map.get('order.cancellation_free_minutes') ?? 120),
    });
    setLoaded(true);
  }, [query.data, loaded, map]);

  return (
    <Card
      title="Аренда, брони и штрафы"
      actions={
        <Button
          disabled={save.isPending}
          onClick={() =>
            save
              .mutateAsync({
                'reservation.hold_ttl_minutes': Number(form.hold),
                'reservation.cash_hold_ttl_minutes': Number(form.cashHold),
                'rental.default_buffer_days': Number(form.buffer),
                'fine.late_fee_multiplier': Number(form.lateFee),
                'order.cancellation_free_minutes': Number(form.freeCancel),
              })
              .then(() => onError(null))
              .catch((e) => onError(errorMessage(e)))
          }
        >
          Сохранить
        </Button>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Держим бронь до оплаты, мин">
          <Input
            value={form.hold}
            inputMode="numeric"
            onChange={(e) => setForm({ ...form, hold: e.target.value })}
          />
        </Field>
        <Field label="При наличной оплате, мин" hint="До подтверждения администратором">
          <Input
            value={form.cashHold}
            inputMode="numeric"
            onChange={(e) => setForm({ ...form, cashHold: e.target.value })}
          />
        </Field>
        <Field label="Буфер на чистку, дней" hint="Если по товару не задан вручную">
          <Input
            value={form.buffer}
            inputMode="numeric"
            onChange={(e) => setForm({ ...form, buffer: e.target.value })}
          />
        </Field>
        <Field label="Пеня за день просрочки" hint="Множитель к суточной стоимости, 1,5–2">
          <Input
            value={form.lateFee}
            inputMode="decimal"
            onChange={(e) => setForm({ ...form, lateFee: e.target.value })}
          />
        </Field>
        <Field label="Бесплатная отмена, мин" hint="От момента оформления заказа">
          <Input
            value={form.freeCancel}
            inputMode="numeric"
            onChange={(e) => setForm({ ...form, freeCancel: e.target.value })}
          />
        </Field>
      </div>
    </Card>
  );
}

function PickupPoints({ onError }: { onError: (m: string | null) => void }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Partial<PickupPoint> | null>(null);

  const { data: points = [] } = useQuery({
    queryKey: ['pickup-points-admin'],
    queryFn: async () => (await api.get<PickupPoint[]>('/admin/pickup-points?all=true')).data,
  });

  const removePoint = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/pickup-points/${id}`),
    onSuccess: () => {
      setDraft(null);
      onError(null);
      void queryClient.invalidateQueries({ queryKey: ['pickup-points-admin'] });
    },
    onError: (e) => onError(errorMessage(e)),
  });

  const save = useMutation({
    mutationFn: (value: Partial<PickupPoint>) =>
      value.id
        ? api.patch(`/admin/pickup-points/${value.id}`, value)
        : api.post('/admin/pickup-points', value),
    onSuccess: () => {
      setDraft(null);
      onError(null);
      void queryClient.invalidateQueries({ queryKey: ['pickup-points-admin'] });
    },
    onError: (e) => onError(errorMessage(e)),
  });

  return (
    <Card
      title="Пункты самовывоза"
      actions={
        <Button
          onClick={() =>
            setDraft({ name: {}, address: {}, lat: 41.311081, lng: 69.240562, isActive: true })
          }
        >
          Добавить
        </Button>
      }
    >
      <div className="space-y-2">
        {points.map((point) => (
          <div
            key={point.id}
            className="flex items-start justify-between gap-3 rounded-lg border border-neutral-200 p-3 text-sm dark:border-neutral-800"
          >
            <div>
              <p className="font-medium">
                {point.name.ru} {point.isOrigin && <Badge tone="green">точка отсчёта</Badge>}
                {!point.isActive && <Badge tone="red">выкл</Badge>}
              </p>
              <p className="text-neutral-500">{point.address.ru}</p>
              <p className="text-xs text-neutral-400">
                {point.lat}, {point.lng} {point.phone && `· ${point.phone}`}
              </p>
            </div>
            <div className="shrink-0">
              <Button variant="ghost" onClick={() => setDraft(point)}>
                Изм.
              </Button>
              {!point.isOrigin && (
                <Button
                  variant="ghost"
                  className="text-red-600"
                  onClick={() => removePoint.mutate(point.id)}
                >
                  Удал.
                </Button>
              )}
            </div>
          </div>
        ))}

        {points.length === 0 && <p className="text-sm text-neutral-500">Пунктов пока нет</p>}
      </div>

      {draft && (
        <div className="mt-4 space-y-3 border-t border-neutral-200 pt-4 dark:border-neutral-800">
          <LocalizedInput
            label="Название"
            value={draft.name ?? {}}
            onChange={(name) => setDraft({ ...draft, name })}
          />
          <LocalizedInput
            label="Адрес"
            value={draft.address ?? {}}
            onChange={(address) => setDraft({ ...draft, address })}
          />

          <div className="grid gap-3 sm:grid-cols-4">
            <Field label="Широта">
              <Input
                value={String(draft.lat ?? '')}
                inputMode="decimal"
                onChange={(e) => setDraft({ ...draft, lat: Number(e.target.value) })}
              />
            </Field>
            <Field label="Долгота">
              <Input
                value={String(draft.lng ?? '')}
                inputMode="decimal"
                onChange={(e) => setDraft({ ...draft, lng: Number(e.target.value) })}
              />
            </Field>
            <Field label="Телефон">
              <Input
                value={draft.phone ?? ''}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              />
            </Field>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <Checkbox
                checked={draft.isOrigin ?? false}
                onChange={(e) => setDraft({ ...draft, isOrigin: e.target.checked })}
                className="size-4"
              />
              Склад, от которого считается доставка
            </label>
            <label className="flex items-center gap-2">
              <Checkbox
                checked={draft.isActive ?? true}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                className="size-4"
              />
              Показывать клиентам
            </label>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDraft(null)}>
              Отмена
            </Button>
            <Button disabled={save.isPending} onClick={() => save.mutate(draft)}>
              Сохранить
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function OfferEditor({ onError }: { onError: (m: string | null) => void }) {
  const queryClient = useQueryClient();
  const [content, setContent] = useState<Localized>({});
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: offers = [] } = useQuery({
    queryKey: ['offers'],
    queryFn: async () => (await api.get<Offer[]>('/admin/offer')).data,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['offers'] });
  const fail = (e: unknown) => onError(errorMessage(e));

  const create = useMutation({
    mutationFn: () => api.post('/admin/offer', { content }),
    onSuccess: () => {
      setContent({});
      onError(null);
      void invalidate();
    },
    onError: fail,
  });

  const update = useMutation({
    mutationFn: (id: string) => api.patch(`/admin/offer/${id}`, { content }),
    onSuccess: () => {
      setEditingId(null);
      setContent({});
      void invalidate();
    },
    onError: fail,
  });

  const publish = useMutation({
    mutationFn: (id: string) => api.post(`/admin/offer/${id}/publish`),
    onSuccess: invalidate,
    onError: fail,
  });

  return (
    <Card title="Публичная оферта">
      <div className="space-y-3">
        <p className="text-xs text-neutral-500">
          Заказ ссылается на принятую редакцию, поэтому опубликованный текст править нельзя —
          создаётся новая версия. Условия уже оформленных заказов не меняются.
        </p>

        <div className="space-y-2">
          {offers.map((offer) => (
            <div
              key={offer.id}
              className="flex items-start justify-between gap-3 rounded-lg border border-neutral-200 p-3 text-sm dark:border-neutral-800"
            >
              <div className="min-w-0">
                <p className="font-medium">
                  Редакция {offer.version}{' '}
                  {offer.isActive ? <Badge tone="green">действует</Badge> : <Badge>черновик</Badge>}
                </p>
                <p className="line-clamp-2 text-xs text-neutral-500">{offer.content.ru}</p>
              </div>

              <div className="flex shrink-0 gap-1">
                {!offer.isActive && (
                  <>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setEditingId(offer.id);
                        setContent(offer.content);
                      }}
                    >
                      Изм.
                    </Button>
                    <Button variant="ghost" onClick={() => publish.mutate(offer.id)}>
                      Опубликовать
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-2 border-t border-neutral-200 pt-3 dark:border-neutral-800">
          <p className="text-sm font-medium">{editingId ? 'Правка черновика' : 'Новая редакция'}</p>

          {(['ru', 'uz', 'en'] as const).map((locale) => (
            <Field key={locale} label={{ ru: 'Русский', uz: "O'zbekcha", en: 'English' }[locale]}>
              <Textarea
                rows={4}
                value={content[locale] ?? ''}
                onChange={(e) => setContent({ ...content, [locale]: e.target.value })}
              />
            </Field>
          ))}

          <div className="flex justify-end gap-2">
            {editingId && (
              <Button
                variant="secondary"
                onClick={() => {
                  setEditingId(null);
                  setContent({});
                }}
              >
                Отмена
              </Button>
            )}
            <Button
              disabled={!content.ru?.trim()}
              onClick={() => (editingId ? update.mutate(editingId) : create.mutate())}
            >
              {editingId ? 'Сохранить черновик' : 'Создать редакцию'}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
